import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  buildSystemGeneralSettingsPayloadFromForm,
  mapSystemGeneralSettingsFieldErrors,
  mapSystemGeneralSettingsResponseToDto,
  validateSystemGeneralSettingsPayload,
} from '../dto/system-general-settings.dto.js';
import {
  buildEmptySystemGeneralSettingsForm,
  mergeBrandingLogoFields,
  validateSystemBrandingLogoFile,
} from '../model/system-general-settings.model.js';

function formsEqual(left, right) {
  return [
    'complianceMode',
    'dpoName',
    'dpoEmail',
    'dpoPhone',
    'dataCenterCountry',
    'dataCenterName',
    'dataCenterAddress',
    'allowedStorageRegionsText',
    'appName',
    'primaryColor',
    'accentColor',
  ].every((key) => String(left?.[key] || '') === String(right?.[key] || ''));
}

export function useSystemGeneralSettings() {
  const { token } = useAuth();

  const [form, setForm] = useState(buildEmptySystemGeneralSettingsForm());
  const [initialForm, setInitialForm] = useState(buildEmptySystemGeneralSettingsForm());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [logoInputKey, setLogoInputKey] = useState(0);
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [isLogoDeleting, setIsLogoDeleting] = useState(false);

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    setFormErrors({});

    try {
      const response = await adminApi.getSystemGeneralSettings(token);
      const nextForm = mapSystemGeneralSettingsResponseToDto(response);
      setForm(nextForm);
      setInitialForm(nextForm);
      setSelectedLogoFile(null);
      setLogoInputKey((key) => key + 1);
    } catch (requestError) {
      const empty = buildEmptySystemGeneralSettingsForm();
      setForm(empty);
      setInitialForm(empty);
      setSelectedLogoFile(null);
      setLogoInputKey((key) => key + 1);
      setError(requestError.message || 'Failed to load general settings');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const onFormChange = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFormErrors((prev) => {
      if (!prev[key]) {
        return prev;
      }

      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const onLogoFileChange = useCallback((file) => {
    const validationError = validateSystemBrandingLogoFile(file);
    setSelectedLogoFile(validationError ? null : file);
    setSuccess('');

    setFormErrors((prev) => {
      const next = { ...prev };
      if (validationError) {
        next.logo = validationError;
      } else {
        delete next.logo;
      }
      return next;
    });
  }, []);

  const resetForm = useCallback(() => {
    if (isSaving) {
      return;
    }

    setForm(initialForm);
    setSelectedLogoFile(null);
    setLogoInputKey((key) => key + 1);
    setFormErrors({});
    setError('');
    setSuccess('');
  }, [initialForm, isSaving]);

  const uploadLogo = useCallback(async () => {
    const validationError = validateSystemBrandingLogoFile(selectedLogoFile);

    if (validationError) {
      setFormErrors((prev) => ({ ...prev, logo: validationError }));
      setError('Please fix validation errors.');
      setSuccess('');
      return;
    }

    setIsLogoUploading(true);
    setError('');
    setSuccess('');
    setFormErrors((prev) => {
      const next = { ...prev };
      delete next.logo;
      return next;
    });

    try {
      const response = await adminApi.uploadSystemBrandingLogo(token, selectedLogoFile);
      const responseForm = mapSystemGeneralSettingsResponseToDto(response);
      setForm((currentForm) => mergeBrandingLogoFields(currentForm, responseForm));
      setInitialForm((currentForm) => mergeBrandingLogoFields(currentForm, responseForm));
      setSelectedLogoFile(null);
      setLogoInputKey((key) => key + 1);
      setSuccess('Branding logo updated.');
    } catch (requestError) {
      setFormErrors(mapSystemGeneralSettingsFieldErrors(requestError));
      setError(requestError.message || 'Failed to upload branding logo');
    } finally {
      setIsLogoUploading(false);
    }
  }, [selectedLogoFile, token]);

  const deleteLogo = useCallback(async () => {
    setIsLogoDeleting(true);
    setError('');
    setSuccess('');
    setFormErrors((prev) => {
      const next = { ...prev };
      delete next.logo;
      return next;
    });

    try {
      const response = await adminApi.deleteSystemBrandingLogo(token);
      const responseForm = mapSystemGeneralSettingsResponseToDto(response);
      setForm((currentForm) => mergeBrandingLogoFields(currentForm, responseForm));
      setInitialForm((currentForm) => mergeBrandingLogoFields(currentForm, responseForm));
      setSelectedLogoFile(null);
      setLogoInputKey((key) => key + 1);
      setSuccess('Branding logo removed.');
    } catch (requestError) {
      setFormErrors(mapSystemGeneralSettingsFieldErrors(requestError));
      setError(requestError.message || 'Failed to remove branding logo');
    } finally {
      setIsLogoDeleting(false);
    }
  }, [token]);

  const saveForm = useCallback(async (event) => {
    event.preventDefault();

    const payload = buildSystemGeneralSettingsPayloadFromForm(form);
    const nextErrors = validateSystemGeneralSettingsPayload(payload);

    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      setError('Please fix validation errors.');
      setSuccess('');
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');
    setFormErrors({});

    try {
      const response = await adminApi.updateSystemGeneralSettings(token, payload);
      const nextForm = mapSystemGeneralSettingsResponseToDto(response);
      setForm(nextForm);
      setInitialForm(nextForm);
      setSuccess('General settings updated.');
    } catch (requestError) {
      setFormErrors(mapSystemGeneralSettingsFieldErrors(requestError));
      setError(requestError.message || 'Failed to update general settings');
    } finally {
      setIsSaving(false);
    }
  }, [form, token]);

  return {
    form,
    isLoading,
    isSaving,
    isLogoUploading,
    isLogoDeleting,
    error,
    success,
    formErrors,
    hasChanges: !formsEqual(form, initialForm),
    selectedLogoFile,
    logoInputKey,
    onFormChange,
    onLogoFileChange,
    uploadLogo,
    deleteLogo,
    resetForm,
    saveForm,
  };
}
